import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Check,
  ArrowUpRight,
  Download,
  Loader2,
  AlertTriangle,
  Zap,
  Shield,
  Crown,
  BarChart3,
  Rocket,
  X,
} from "lucide-react";
import Layout, { PageContainer, PageHeader } from "../components/ui/Layout";
import {
  getBillingConfig,
  getSubscription,
  getUsage,
  getInvoices,
  createCheckout,
  createPortalSession,
  verifyCheckout,
  planDisplayName,
  salesMailto,
  formatLimit,
  formatUploadCap,
  PLAN_ORDER,
  type PlanName,
  type PaidPlanName,
  type BillingPlan,
} from "../api/billing";
import { cn } from "../lib/utils";

// shadcn/ui components
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../components/shadcn/card";
import { Badge } from "../components/shadcn/badge";
import { Button } from "../components/shadcn/button";
import { Progress } from "../components/shadcn/progress";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/shadcn/table";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const FALLBACK_CONTACT = "yash.sancheti@paxleysoftware.com";

const PLAN_ICON: Record<PlanName, { icon: React.ReactNode; color: string }> = {
  free: { icon: <Shield className="h-5 w-5" />, color: "text-muted-foreground" },
  starter: { icon: <Zap className="h-5 w-5" />, color: "text-primary" },
  growth: { icon: <Rocket className="h-5 w-5" />, color: "text-primary" },
  enterprise: { icon: <Crown className="h-5 w-5" />, color: "text-amber-400" },
};

const STATUS_BADGE_VARIANT: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  active: { label: "Active", variant: "default" },
  trialing: { label: "Trial", variant: "secondary" },
  past_due: { label: "Past Due", variant: "destructive" },
  canceled: { label: "Canceled", variant: "outline" },
  expired: { label: "Expired", variant: "outline" },
  none: { label: "No subscription", variant: "outline" },
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const daysLeft = (iso: string) =>
  Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));

/** The bullets a plan card shows: caps first, then the features it adds over the tier below. */
function planBullets(plan: PlanName, plans: Record<PlanName, BillingPlan>, featureDisplay: Record<string, string>): string[] {
  const p = plans[plan];
  const below = PLAN_ORDER[PLAN_ORDER.indexOf(plan) - 1];
  const baseline = below ? new Set(plans[below].features) : new Set<string>();
  const added = p.features.filter((f) => !baseline.has(f));
  const bullets = [
    `${formatLimit(p.maxRepos)} ${p.maxRepos === 1 ? "repository" : "repositories"}`,
    p.maxScansPerMonth === -1 ? "Continuous scanning" : `${p.maxScansPerMonth} scans per month`,
    p.maxMembers === -1 ? "Your whole team included" : `${p.maxMembers} team members`,
    `${formatUploadCap(p.maxUploadBytes)} per upload`,
  ];
  if (below) bullets.push(`Everything in ${plans[below].displayName}`);
  // Code Health pillars collapse to one line so the card stays scannable.
  const codeHealth = ["code_health", "commit_history_analytics", "framework_fragmentation", "stale_code_detection", "sonarqube_integration"];
  let codeHealthShown = false;
  for (const id of added) {
    if (codeHealth.includes(id)) {
      if (!codeHealthShown) { bullets.push("Code Health & maintainability"); codeHealthShown = true; }
      continue;
    }
    bullets.push(featureDisplay[id] ?? id);
  }
  return bullets;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export default function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const checkoutStatus = searchParams.get("checkout");

  const configQuery = useQuery({
    queryKey: ["billing", "config"],
    queryFn: getBillingConfig,
  });
  const subQuery = useQuery({
    queryKey: ["billing", "subscription"],
    queryFn: getSubscription,
  });
  const usageQuery = useQuery({
    queryKey: ["billing", "usage"],
    queryFn: getUsage,
  });
  const invoiceQuery = useQuery({
    queryKey: ["billing", "invoices"],
    queryFn: () => getInvoices().then((r) => r.invoices),
  });

  const config = configQuery.data;
  const oneTime = config?.billingModel === "one_time";
  const passDays = config?.oneTimeDays ?? 30;
  const contactEmail = config?.contactEmail || FALLBACK_CONTACT;

  const sub = subQuery.data;
  const usage = usageQuery.data;
  const invoices = invoiceQuery.data;
  const currentPlan = (sub?.plan || "free") as PlanName;
  const isEnterprise = currentPlan === "enterprise";
  const planIcon = PLAN_ICON[currentPlan] ?? PLAN_ICON.free;
  const planName = planDisplayName(currentPlan, config);
  const statusInfo =
    STATUS_BADGE_VARIANT[sub?.subscription.status || "none"] ||
    STATUS_BADGE_VARIANT.none;
  const periodEnd = sub?.subscription.currentPeriodEnd || null;

  const handleCheckout = async (plan: PaidPlanName) => {
    setCheckoutLoading(plan);
    try {
      const { url } = await createCheckout(plan, "monthly");
      window.location.href = url;
    } catch {
      setCheckoutLoading(null);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch {
      setPortalLoading(false);
    }
  };

  // Verify transaction on redirect back
  useEffect(() => {
    const reference = searchParams.get("reference") || searchParams.get("trxref");
    if (reference && checkoutStatus === "success") {
      verifyCheckout(reference)
        .then(() => {
          subQuery.refetch();
          usageQuery.refetch();
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissCheckoutBanner = () => {
    searchParams.delete("checkout");
    searchParams.delete("reference");
    searchParams.delete("trxref");
    setSearchParams(searchParams, { replace: true });
  };

  // Everything below reads `sub`, and `sub?.plan || "free"` turns *any* absence
  // of that response into a confident "Community" with purchase buttons.
  // Absence is not a plan: show the real reason instead.
  const subStatus = (subQuery.error as { status?: number } | null)?.status;

  if (subQuery.isLoading) {
    return (
      <Layout>
        <PageContainer>
          <PageHeader title="Billing" description="Loading your plan…" />
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </PageContainer>
      </Layout>
    );
  }

  if (subStatus === 401 || subStatus === 403) {
    return (
      <Layout>
        <PageContainer>
          <PageHeader title="Billing" description="Plan and payment details" />
          <Card>
            <CardContent className="p-12 text-center">
              <Shield className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm font-semibold">Billing is admin-only</p>
              <p className="text-sm text-muted-foreground mt-2">
                Your role does not have access to this organization's plan or payment
                details. Ask an organization admin if you need them.
              </p>
            </CardContent>
          </Card>
        </PageContainer>
      </Layout>
    );
  }

  if (subQuery.isError) {
    return (
      <Layout>
        <PageContainer>
          <PageHeader title="Billing" description="Plan and payment details" />
          <Card>
            <CardContent className="p-12 text-center">
              <AlertTriangle className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm font-semibold">Could not load your billing details</p>
              <p className="text-sm text-muted-foreground mt-2">
                Your plan is unchanged, we just could not read it right now.
              </p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => subQuery.refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer>
        <PageHeader
          title="Billing & Plan"
          description={
            isEnterprise
              ? "Your plan, usage, and account details"
              : "Your plan, usage, and invoices"
          }
        />

        {/* Checkout Success Banner */}
        {checkoutStatus === "success" && (
          <Card className="mb-6 border-l-4 border-l-green-500 bg-green-500/5">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                    {oneTime ? "Plan access activated" : "Subscription activated"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {oneTime
                      ? `You've got ~${passDays} days of access. Thank you!`
                      : "Your plan has been upgraded. Thank you!"}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={dismissCheckoutBanner}>
                <X className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Checkout Canceled Banner */}
        {checkoutStatus === "canceled" && (
          <Card className="mb-6 border-l-4 border-l-yellow-500 bg-yellow-500/5">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <p className="text-sm text-muted-foreground">
                  Checkout was canceled. No changes were made.
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={dismissCheckoutBanner}>
                <X className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Past Due Warning (subscription billing only) */}
        {!oneTime && sub?.subscription.status === "past_due" && (
          <Card className="mb-6 border-l-4 border-l-destructive bg-destructive/5">
            <CardContent className="flex items-center gap-3 p-4">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-destructive">Payment failed</p>
                <p className="text-xs text-muted-foreground">
                  Please update your payment method to avoid service interruption.
                </p>
              </div>
              <Button size="sm" onClick={handlePortal}>
                Update Payment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Access-ending-soon nudge (fixed-term access; not for managed plans) */}
        {oneTime &&
          currentPlan !== "free" &&
          !isEnterprise &&
          periodEnd &&
          daysLeft(periodEnd) <= 7 && (
            <Card className="mb-6 border-l-4 border-l-emerald-500 bg-emerald-500/5">
              <CardContent className="flex items-center gap-3 p-4">
                <AlertTriangle className="h-5 w-5 text-emerald-400" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-300">
                    {daysLeft(periodEnd) === 0
                      ? `Your ${planName} access ends today`
                      : `Your ${planName} access ends in ${daysLeft(periodEnd)} day${daysLeft(periodEnd) === 1 ? "" : "s"}`}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Email us to renew or extend your plan.
                  </p>
                </div>
                <Button size="sm" variant="outline" asChild>
                  <a href={`mailto:${contactEmail}?subject=Extend%20my%20Paxley%20${encodeURIComponent(planName)}%20access`}>
                    Ask for an extension
                  </a>
                </Button>
              </CardContent>
            </Card>
          )}

        <div className="space-y-8">
          {/* ── Current Plan Card ─────────────────────────────────────── */}
          <Card>
            <CardContent className="flex items-center justify-between p-6">
              <div className="flex items-center gap-4">
                <div className={cn("p-3 rounded-xl bg-secondary", planIcon.color)}>
                  {planIcon.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-foreground">{planName} Plan</h2>
                    <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                  </div>

                  {sub?.limits && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatLimit(sub.limits.maxRepos)} repositories ·{" "}
                      {sub.limits.maxScansPerMonth === -1 ? "continuous scanning" : `${sub.limits.maxScansPerMonth} scans / month`} ·{" "}
                      {formatUploadCap(sub.limits.maxUploadBytes)} per upload
                    </p>
                  )}

                  {/* Trial (subscription) */}
                  {!oneTime &&
                    sub?.subscription.status === "trialing" &&
                    sub.subscription.trialEndsAt && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Trial ends {fmtDate(sub.subscription.trialEndsAt)}
                      </p>
                    )}

                  {/* Fixed-term access window */}
                  {oneTime && currentPlan !== "free" && periodEnd && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Access until{" "}
                      <span className="text-foreground font-medium">{fmtDate(periodEnd)}</span>
                      <span className="text-emerald-600 dark:text-emerald-400">
                        {" "}· {daysLeft(periodEnd)} day{daysLeft(periodEnd) === 1 ? "" : "s"} left
                      </span>
                    </p>
                  )}

                  {/* Subscription renew/cancel */}
                  {!oneTime &&
                    periodEnd &&
                    sub?.subscription.status === "active" && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {sub.subscription.cancelAtPeriodEnd ? "Cancels" : "Renews"}{" "}
                        {fmtDate(periodEnd)}
                      </p>
                    )}
                </div>
              </div>
              <div className="flex gap-2">
                {/* Subscription customers can manage via the provider portal */}
                {!oneTime && sub?.subscription.hasCustomer && (
                  <Button
                    variant="outline"
                    onClick={handlePortal}
                    disabled={portalLoading}
                    className="gap-2"
                  >
                    {portalLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowUpRight className="h-4 w-4" />
                    )}
                    Manage Subscription
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ── Usage Meters ──────────────────────────────────────────── */}
          {usage && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" /> Current Usage
              </h3>
              <div className="grid gap-4 sm:grid-cols-3">
                <UsageMeterCard
                  label="Repositories"
                  current={usage.usage.repos.current}
                  limit={usage.usage.repos.limit}
                />
                <UsageMeterCard
                  label="Scans this month"
                  current={usage.usage.scansThisMonth.current}
                  limit={usage.usage.scansThisMonth.limit}
                />
                <UsageMeterCard
                  label="Team members"
                  current={usage.usage.members.current}
                  limit={usage.usage.members.limit}
                />
              </div>
            </div>
          )}

          {/* ── Enterprise: access is managed, no self-serve pricing ───── */}
          {isEnterprise && (
            <Card className="border-brand/25 bg-brand/5">
              <CardContent className="flex items-center gap-4 p-6">
                <div className="p-3 rounded-xl bg-brand/15 text-brand">
                  <Crown className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text">
                    Your organization has full platform access
                  </p>
                  <p className="text-xs text-muted mt-1">
                    All capabilities are enabled: unlimited repositories, scans, and team
                    members. Your subscription is managed directly with Nucleus Systems;
                    there is nothing to configure here.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Plans (hidden for managed enterprise access) ───────────── */}
          {!isEnterprise && config && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">Plans</h3>
                <p className="text-xs text-muted-foreground">
                  Priced around the software you protect, never per seat.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {(config.planOrder ?? PLAN_ORDER).map((planId) => {
                  const p = config.plans[planId];
                  if (!p) return null;
                  return (
                    <PlanCard
                      key={planId}
                      planId={planId}
                      plan={p}
                      bullets={planBullets(planId, config.plans, config.featureDisplay)}
                      current={currentPlan === planId}
                      highlighted={planId === "growth"}
                      contactEmail={contactEmail}
                      onCheckout={() => handleCheckout(planId as PaidPlanName)}
                      loading={checkoutLoading === planId}
                    />
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Starter, Growth and Enterprise are self-hosted installations, so your source code stays
                in your environment. One-off code security risk assessments for M&amp;A start at $1,500
                per project. Full details at{" "}
                <a className="underline" href="https://paxleysoftware.com/pricing" target="_blank" rel="noreferrer">
                  paxleysoftware.com/pricing
                </a>.
              </p>
            </div>
          )}

          {/* ── Receipt / Invoice History ─────────────────────────────── */}
          {invoices && invoices.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">
                  {oneTime ? "Purchase History" : "Invoice History"}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[11px] font-semibold uppercase">Date</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase">Amount</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase">Status</TableHead>
                      <TableHead className="text-right text-[11px] font-semibold uppercase">
                        Receipt
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="text-foreground">
                          {fmtDate(inv.created_at)}
                        </TableCell>
                        <TableCell className="text-foreground font-medium">
                          ${(inv.amount_due / 100).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={inv.status === "paid" ? "default" : "secondary"}
                            className="uppercase text-[10px]"
                          >
                            {inv.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {inv.invoice_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              asChild
                              className="gap-1 text-xs h-7"
                            >
                              <a href={inv.invoice_url} target="_blank" rel="noopener noreferrer">
                                <Download className="h-3 w-3" /> View
                              </a>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </PageContainer>
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function UsageMeterCard({
  label,
  current,
  limit,
}: {
  label: string;
  current: number;
  limit: number;
}) {
  const isUnlimited = limit === -1;
  const pct = isUnlimited ? 0 : Math.min(100, (current / Math.max(limit, 1)) * 100);
  const isNearLimit = !isUnlimited && pct >= 80;
  const isAtLimit = !isUnlimited && current >= limit;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex justify-between items-baseline mb-3">
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
          <span
            className={cn(
              "text-sm font-bold",
              isAtLimit
                ? "text-destructive"
                : isNearLimit
                  ? "text-yellow-500"
                  : "text-foreground"
            )}
          >
            {current}{" "}
            <span className="text-muted-foreground font-normal">
              / {isUnlimited ? "Unlimited" : limit}
            </span>
          </span>
        </div>
        {!isUnlimited && (
          <Progress
            value={pct}
            className={cn(
              "h-2",
              isAtLimit
                ? "[&>div]:bg-destructive"
                : isNearLimit
                  ? "[&>div]:bg-yellow-500"
                  : ""
            )}
          />
        )}
      </CardContent>
    </Card>
  );
}

function PlanCard({
  planId,
  plan,
  bullets,
  current,
  highlighted,
  contactEmail,
  onCheckout,
  loading,
}: {
  planId: PlanName;
  plan: BillingPlan;
  bullets: string[];
  current: boolean;
  highlighted?: boolean;
  contactEmail: string;
  onCheckout: () => void;
  loading?: boolean;
}) {
  const isFree = planId === "free";
  const price = Math.round(plan.priceMonthlyCents / 100);
  const ctaLabel = planId === "enterprise" ? "Contact sales" : `Get ${plan.displayName}`;

  return (
    <Card
      className={cn(
        "flex flex-col",
        highlighted && !current && "ring-2 ring-primary",
        current && "ring-2 ring-green-500/40 border-green-500/20"
      )}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{plan.displayName}</CardTitle>
          {current ? (
            <Badge
              variant="default"
              className="bg-green-500/15 text-green-600 dark:text-green-400 border-transparent text-[10px] uppercase"
            >
              Current
            </Badge>
          ) : highlighted ? (
            <Badge variant="secondary" className="text-[10px] uppercase">Most popular</Badge>
          ) : null}
        </div>
        <div className="mt-2">
          {isFree ? (
            <span className="text-3xl font-extrabold text-foreground">Free</span>
          ) : (
            <>
              <span className="text-3xl font-extrabold text-foreground">${price}</span>
              <span className="text-sm text-muted-foreground"> / month</span>
            </>
          )}
          <p className="text-[11px] text-muted-foreground mt-0.5">{plan.contract}</p>
        </div>
        <p className="text-xs text-muted-foreground mt-2">{plan.bestFor}</p>
      </CardHeader>

      <CardContent className="flex-1">
        <ul className="space-y-2">
          {bullets.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter>
        {current ? (
          <p className="text-center text-xs text-muted-foreground w-full py-2">Your current plan</p>
        ) : isFree ? (
          <p className="text-center text-xs text-muted-foreground w-full py-2">Default plan</p>
        ) : plan.purchasable ? (
          <Button
            onClick={onCheckout}
            disabled={loading}
            variant={highlighted ? "default" : "outline"}
            className="w-full gap-2"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Upgrade to {plan.displayName}
          </Button>
        ) : (
          <Button asChild variant={highlighted ? "default" : "outline"} className="w-full gap-2">
            <a href={salesMailto(planId, contactEmail)}>
              <ArrowUpRight className="h-4 w-4" /> {ctaLabel}
            </a>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
